import { Component, inject, ChangeDetectionStrategy } from '@angular/core';

import { Router } from '@angular/router';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Store, select } from '@ngrx/store';
import { selectUser } from '@workspace-app/state/global';
import { LicenseAuthorizationService } from '../../services/license-authorization.service';

@Component({
  selector: 'app-renewal-status-cell-renderer',
  standalone: true,
  imports: [TagModule, TooltipModule],
  template: `
    @if (status) {
      <p-tag
        [value]="label"
        [severity]="severity"
        [icon]="icon"
        (click)="onClick()"
        [style.cursor]="isClickable ? 'pointer' : 'default'"
        [pTooltip]="tooltip"
        styleClass="compact-tag">
      </p-tag>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: flex;
        align-items: center;
        height: 100%;
      }

      :host ::ng-deep .compact-tag {
        display: inline-flex !important;
        font-size: 0.8rem;
        padding: 0.25rem 0.5rem;
        line-height: 1;
        height: auto !important;
        min-height: unset !important;
      }

      :host ::ng-deep .compact-tag .p-tag-value {
        line-height: 1;
      }

      :host ::ng-deep .compact-tag .p-tag-icon {
        font-size: 0.7rem;
        margin-right: 0.25rem;
      }
    `,
  ],
})
export class RenewalStatusCellRendererComponent implements ICellRendererAngularComp {
  private router = inject(Router);
  private store = inject(Store);
  private licenseAuthService = inject(LicenseAuthorizationService);

  public params!: ICellRendererParams;
  public status: string | null = null;
  public label: string = '';
  public severity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined = 'secondary';
  public icon: string = '';
  public tooltip: string = '';
  public isClickable: boolean = false;
  private canManageRenewals: boolean = false;

  constructor() {
    this.store.pipe(select(selectUser)).subscribe((user) => {
      this.canManageRenewals = this.licenseAuthService.canManageRenewalWorkflows(user ?? null);
    });
  }

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.refresh(params);
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    const license = params.data;

    // Get the renewal display status
    this.status = license?.renewalDisplayStatus || null;

    if (!this.status) {
      return true;
    }

    const year = license?.lastReview?.certificationYear;
    // Only clickable if user has permission to manage renewal workflows
    this.isClickable = !!license?.lastReview?.id && this.canManageRenewals;

    // Configure display based on status
    switch (this.status) {
      case 'In Progress':
        this.label = year ? `In Progress (${year})` : 'In Progress';
        this.severity = 'info';
        this.icon = 'pi pi-spin pi-spinner';
        this.tooltip = 'License renewal is being reviewed';
        break;

      case 'Certificate Ready': {
        // Check if certificate was already sent (has valid token)
        const hasToken = license?.hasValidCertificateToken;
        if (hasToken) {
          this.label = year ? `Certificate Sent (${year})` : 'Certificate Sent';
          this.severity = 'success';
          this.icon = 'pi pi-check-circle';
          this.tooltip = 'Certificate has been sent to GLs';
        } else {
          this.label = year ? `Certificate Ready (${year})` : 'Certificate Ready';
          this.severity = 'warn';
          this.icon = 'pi pi-file';
          this.tooltip = 'Certificate is ready for distribution';
        }
        break;
      }

      case 'Cancelled':
        this.label = 'Cancelled';
        this.severity = 'secondary';
        this.icon = 'pi pi-times';
        this.tooltip = 'Renewal process was cancelled';
        break;

      default:
        this.label = this.status;
        this.severity = 'secondary';
        this.icon = '';
    }

    return true;
  }

  onClick(): void {
    if (this.isClickable && this.params.data?.lastReview?.id) {
      // Navigate to the renewal workflow
      this.router.navigate(['/certification/licenses/extension-reviews', this.params.data.lastReview.id]);
    }
  }
}
